// src/pages/tickets/components/TicketAcciones.tsx
import { useState } from 'react';
import {
  Card,
  Button,
  Textarea,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui';
import { useCambiarEstadoTicket } from '@/hooks/queries/useTickets';
import type { Ticket, EstadoTicket } from '@/types';

interface Props {
  ticket: Ticket;
}

const TRANSICIONES: Record<EstadoTicket, EstadoTicket[]> = {
  NUEVO: ['EN_ANALISIS', 'CANCELADO'],
  EN_ANALISIS: ['EN_DESARROLLO', 'RESUELTO', 'CANCELADO'],
  EN_DESARROLLO: ['EN_PRUEBAS', 'EN_ANALISIS', 'CANCELADO'],
  EN_PRUEBAS: ['RESUELTO', 'EN_DESARROLLO', 'CANCELADO'],
  RESUELTO: ['CERRADO', 'EN_ANALISIS'],
  CERRADO: [],
  CANCELADO: [],
};

const ESTADO_LABELS: Record<EstadoTicket, string> = {
  NUEVO: 'Nuevo',
  EN_ANALISIS: 'En Analisis',
  EN_DESARROLLO: 'En Desarrollo',
  EN_PRUEBAS: 'En Pruebas',
  RESUELTO: 'Resuelto',
  CERRADO: 'Cerrado',
  CANCELADO: 'Cancelado',
};

export function TicketAcciones({ ticket }: Props) {
  const cambiarEstado = useCambiarEstadoTicket();

  const [estadoSeleccionado, setEstadoSeleccionado] = useState<EstadoTicket | ''>('');
  const [comentario, setComentario] = useState('');

  const transicionesPosibles = TRANSICIONES[ticket.estado];

  const handleCambiarEstado = async () => {
    if (!estadoSeleccionado) return;

    await cambiarEstado.mutateAsync({
      id: ticket.id,
      data: { estado: estadoSeleccionado, comentario: comentario || undefined },
    });

    setEstadoSeleccionado('');
    setComentario('');
  };

  if (ticket.estado === 'CERRADO' || ticket.estado === 'CANCELADO') {
    return (
      <Card className="p-4 bg-gray-50">
        <p className="text-sm text-gray-500 text-center">
          Este ticket esta {ticket.estado === 'CERRADO' ? 'cerrado' : 'cancelado'}
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <h3 className="font-semibold mb-3">Acciones</h3>

      <div className="space-y-3">
        <div className="space-y-2">
          <Label>Cambiar Estado</Label>
          <Select
            value={estadoSeleccionado || 'none'}
            onValueChange={(value) =>
              setEstadoSeleccionado(value === 'none' ? '' : (value as EstadoTicket))
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="Seleccione..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Seleccione...</SelectItem>
              {transicionesPosibles.map((e) => (
                <SelectItem key={e} value={e}>
                  {ESTADO_LABELS[e]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {estadoSeleccionado && (
          <>
            <div className="space-y-2">
              <Label>Comentario (opcional)</Label>
              <Textarea
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                placeholder="Motivo del cambio..."
                rows={2}
              />
            </div>
            <Button
              onClick={handleCambiarEstado}
              loading={cambiarEstado.isPending}
              className="w-full"
            >
              Cambiar a {ESTADO_LABELS[estadoSeleccionado]}
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}
